import { CLIENT_ORIGIN } from './constants';
import type { RepoFullName, Target } from '~/types/github';
import { invariant } from '~/utils/invariant';
import { logger } from '~/utils/logger';

const DUMMY_FILE = '.keep';

// 35 MiB, as GraphQL sometimes fails with "payload cannot exceed 45MB"
const PAYLOAD_SIZE_THRESHOLD = 35 * 2 ** 20;

interface GitHubToken {
  refreshCredentialsAt: number;
  token: string | null;
  scopes: string[];
}

let cachedToken: GitHubToken | undefined;

let ongoingRequest: Promise<GitHubToken | undefined> | undefined;

export async function getGithubToken(params: { sbToken?: string; orgs?: boolean } = {}) {
  if (cachedToken?.token != null) {
    const now = Math.floor(Date.now() / 1000);

    if (cachedToken.refreshCredentialsAt == null || cachedToken.refreshCredentialsAt > now) {
      return cachedToken.token;
    } else {
      cachedToken = undefined;
    }
  }

  let retriever = false;

  try {
    if (!ongoingRequest) {
      ongoingRequest = requestGithubToken(params);
      retriever = true;
    }

    const payload = await ongoingRequest;

    if (retriever) {
      cachedToken = payload;
    }

    return payload?.token ?? undefined;
  } finally {
    if (retriever) {
      ongoingRequest = undefined;
    }
  }
}

async function requestGithubToken({ sbToken, orgs }: { sbToken?: string; orgs?: boolean } = {}) {
  if (!sbToken && !import.meta.env.SSR) {
    const tokenImpl = await import('./stores/token');

    sbToken = await tokenImpl.getToken();
  }

  if (!sbToken) {
    return undefined;
  }

  const url = new URL(CLIENT_ORIGIN);

  url.pathname = `/api/user/tokens/github`;

  url.searchParams.append('scope', orgs ? 'repo,read:org' : 'repo');
  url.searchParams.append('type', 'oauth');

  const response = await fetchImpl(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sbToken}`,
    },
  });

  if (!response.ok) {
    logger.error('Invalid token response', response.status, response);

    return undefined;
  }

  return await response.json<GitHubToken>().catch(() => undefined);
}

interface CommitTarget extends Target {
  expectedHeadOid: string;
}

interface FileChanges {
  additions: Array<{
    path: string;
    contents: string;
  }>;
  deletions: Array<{
    path: string;
  }>;
}

export interface Commit {
  commitMessage: string;
  fileChanges: FileChanges;
}

export async function createRepository(target: { name: string; ownerId?: string }, token: string) {
  const query = `
    mutation($input: CreateRepositoryInput!) {
      createRepository(input: $input) {
        repository {
          id
        }
      }
    }
  `;

  const response = await graphqlRequest(
    query,
    {
      input: {
        name: target.name,
        ownerId: target.ownerId,

        // FIXME: is this still true with GH orgs?
        visibility: 'PRIVATE',
      },
    },
    token,
  );

  const result = await graphqlResponse<{ createRepository: { repository: { id: string } } }>(response);

  return result.createRepository.repository;
}

export type Organization = Awaited<ReturnType<typeof getOrganizations>>[0];

export async function getOrganizations(token: string) {
  const query = `
    query {
      viewer {
        organizations(first: 100) {
          nodes {
            id,
            login,
            viewerCanCreateRepositories,
            avatarUrl,
          }
        }
      }
    }
  `;

  const response = await graphqlRequest(query, {}, token);

  const result = await graphqlResponse<{
    viewer: {
      organizations: {
        nodes: Array<{ id: string; login: string; viewerCanCreateRepositories?: boolean; avatarUrl?: string }>;
      };
    };
  }>(response);

  return result.viewer.organizations.nodes;
}

export async function getUserOrOrganization(login: string, token: string) {
  const query = `
      query GetUserOrOrg($login: String!) {
        user(login: $login) {
          avatarUrl
        }
        organization(login: $login) {
          avatarUrl
        }
      }
  `;

  const response = await graphqlRequest(query, { login }, token);

  interface Data {
    avatarUrl?: string;
  }

  const { data, errors } = await rawGraphqlResponse<{
    data?: {
      organization: null | Data;
      user: null | Data;
    };
    errors?: any[];
  }>(response);

  if (!(data?.organization || data?.user)) {
    throw new GraphQLError(errors ?? []);
  }

  return data?.organization ?? data?.user;
}

/**
 * - The GraphQL API does not allow you to create root commits.
 * - The REST API does not allow you to create commits from a simple list of changes.
 *
 * Hence, this convoluted flow.
 */
export async function createFirstCommit(target: Omit<CommitTarget, 'expectedHeadOid'>, commit: Commit, token: string) {
  // "empty" root commit
  const emptyCommitId = await createEmptyRootCommit(target, token);

  let ref = await getReference(target, token);

  if (ref) {
    await forceUpdateRef({ refId: ref.id, oid: emptyCommitId }, token);
  } else {
    await restRequest(`/repos/${target.repository.owner}/${target.repository.name}/git/refs`, {
      method: 'POST',
      body: JSON.stringify({ ref: `refs/heads/${target.branchName}`, sha: emptyCommitId }),
      token,
    });

    ref = await getReference(target, token);
  }

  const fileChanges = { ...commit.fileChanges };

  if (fileChanges.additions.every((addition) => addition.path !== DUMMY_FILE)) {
    fileChanges.deletions = [{ path: DUMMY_FILE }];
  }

  let intermediateCommitId = emptyCommitId;

  const batches = splitIntoBatches(fileChanges);

  // push changes to branch
  for (const batch of batches) {
    const head = intermediateCommitId;

    const {
      commit: { oid },
    } = await createCommitOnBranch({ ...target, expectedHeadOid: head }, { ...commit, fileChanges: batch }, token);

    intermediateCommitId = oid;
  }

  // grab the tree from the new commit, that has the exact contents we want
  const {
    tree: { sha: treeId },
  } = await restRequest<{ tree: { sha: string } }>(
    `/repos/${target.repository.owner}/${target.repository.name}/git/commits/${intermediateCommitId}`,
    {
      token,
    },
  );

  // create new root commit with those exact same contents
  const { sha: commitId } = await createRootCommit(target.repository, commit.commitMessage, treeId, token);

  if (!ref) {
    throw new Error('Unable to find reference');
  }

  // force-push the branch to this root commit
  await forceUpdateRef({ refId: ref.id, oid: commitId }, token);

  return commitId;
}

/**
 * @private
 */
export function splitIntoBatches(fileChanges: FileChanges, threshold = PAYLOAD_SIZE_THRESHOLD) {
  const parts = [
    ...fileChanges.additions.map((addition) => ({ addition })),
    ...fileChanges.deletions.map((deletion) => ({ deletion })),
  ];

  const batches: FileChanges[] = [];

  let batch: FileChanges = { additions: [], deletions: [] };
  let size = 0;

  for (const part of parts) {
    const partSize =
      'addition' in part ? part.addition.contents.length + part.addition.path.length : part.deletion.path.length;

    if (size + partSize > threshold) {
      batches.push(batch);
      batch = { additions: [], deletions: [] };
      size = 0;
    }

    size += partSize;

    if ('addition' in part) {
      batch.additions.push(part.addition);
    } else {
      batch.deletions.push(part.deletion);
    }
  }

  batches.push(batch);

  return batches;
}

export async function commitFiles(target: CommitTarget, commit: Commit, token: string) {
  try {
    return await createCommitOnBranch(target, commit, token);
  } catch (error) {
    const needsForcePush = error instanceof GraphQLError && error.contains('STALE_DATA');

    if (!needsForcePush) {
      throw error;
    }
  }

  return undefined;
}

export type ViewerPermission = 'ADMIN' | 'MAINTAIN' | 'WRITE' | 'TRIAGE' | 'READ';

export async function getRepositoryByName(repo: RepoFullName, token: string) {
  const response = await restRequest<{ size: number }>(`/repos/${repo.owner}/${repo.name}`, { token });

  return response;
}

export async function getRepository(id: string, token: string) {
  const query = `
    query($id: ID!) {
      node(id: $id) {
        ... on Repository {
          name,
          viewerPermission,
          defaultBranchRef {
            name
          }
        }
      }
    }
  `;

  const variables = {
    id,
  };

  const response = await graphqlRequest(query, variables, token);

  const payload = await graphqlResponse<{
    node: { name: string; viewerPermission: ViewerPermission; defaultBranchRef?: { name: string } };
  }>(response);

  return payload.node;
}

export async function getRepositoryId(repo: RepoFullName, token: string) {
  const query = `
    query($owner: String!, $name: String!) {
      repository(owner: $owner, name: $name) {
        id
      }
    }
  `;

  const response = await graphqlRequest(query, repo, token);

  const payload = await graphqlResponse<{ repository: { id: string } }>(response);

  return payload.repository.id;
}

export async function getDefaultBranch(repo: RepoFullName, token: string) {
  const query = `
    query($owner: String!, $name: String!) {
      repository(owner: $owner, name: $name) {
        defaultBranchRef {
          name
        }
      }
    }
  `;

  const response = await graphqlRequest(query, repo, token);

  const { repository } = await graphqlResponse<{ repository: { defaultBranchRef?: { name: string } } }>(response);

  return repository.defaultBranchRef;
}

export async function getReference({ repository: { owner, name }, branchName }: Target, token: string) {
  const query = `
    query($owner: String!, $name: String!, $qualifiedName: String!) {
      repository(owner: $owner, name: $name) {
        ref(qualifiedName: $qualifiedName) {
          id,
          target {
            oid
          }
        }
      }
    }
  `;

  const variables = {
    owner,
    name,
    qualifiedName: `refs/heads/${branchName}`,
  };

  const response = await graphqlRequest(query, variables, token);

  const payload = await graphqlResponse<{ repository: { ref: { id: string; target?: { oid: string } } | null } }>(
    response,
  );

  return payload.repository.ref;
}
export async function getBranches(target: Target, token: string) {
  const query = `query GetBranches($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      refs(refPrefix: "refs/heads/", first: 100) {
      edges {
        node {
            name
          }
        }
      }
    }
  }`;

  const variables = {
    owner: target.repository.owner,
    name: target.repository.name,
  };

  const response = await graphqlRequest(query, variables, token);

  const payload = await graphqlResponse<{ repository: { refs: { edges: Array<{ node: { name: string } }> } } }>(
    response,
  );

  return payload.repository.refs.edges.map((entry) => entry.node.name);
}

export async function getTarball(target: Target, ref: string, token: string) {
  let response: Response;

  if (import.meta.env.SSR) {
    response = await fetchImpl(
      `https://api.github.com/repos/${target.repository.owner}/${target.repository.name}/tarball/${ref}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'User-Agent': 'bolt.new',
        },
      },
    );
  } else {
    response = await fetchImpl('/api/github', {
      method: 'POST',
      body: JSON.stringify({ ...target.repository, ref, token }),
    });
  }

  if (!response.ok || !response.body) {
    throw new Error('Failed tarball request', { cause: new Error(await response.text()) });
  }

  return response.body;
}

/**
 * Creating a dummy root commit is tricky, as the GitHub API does not document well
 * the different constraints around its APIs.
 *
 * WARNING: this function assumes you are about to wipe the contents of this branch if any,
 * as it modifies them freely.
 */
async function createEmptyRootCommit(
  { repository: { owner, name }, branchName }: Omit<CommitTarget, 'expectedHeadOid'>,
  token: string,
) {
  try {
    /**
     * If the repo is empty, we _have_ to initialize using this endpoint. Creating
     * a commit "manually" seems to fail.
     */
    const {
      commit: { sha: commitId, parents },
    } = await restRequest<{ commit: { sha: string; parents: Array<{}> } }>(
      `/repos/${owner}/${name}/contents/${DUMMY_FILE}`,
      {
        method: 'PUT',
        body: JSON.stringify({ message: 'init', content: btoa('.keep'), branch: branchName }),
        token,
      },
    );

    /**
     * This modification can either mutate existing contents by random luck, or truly initialize with a root commit. If the latter,
     * our job is done. Otherwise we need to keep going.
     */
    if (parents.length === 0) {
      return commitId;
    }
  } catch (error) {
    if (error instanceof Error && error.cause instanceof Response && error.cause.status === 422) {
      /**
       * If the repo is not empty, it's likely that we will hit this error path (as our dummy file is always the same).
       * We have to create the root commit manually, so we ignore the error.
       */
    } else {
      throw error;
    }
  }

  // here we create a root commit "manually"

  const { sha: blobSha } = await restRequest<{ sha: string }>(`/repos/${owner}/${name}/git/blobs`, {
    token,
    method: 'POST',
    body: JSON.stringify({ content: '' }),
  });

  const { sha: treeSha } = await restRequest<{ sha: string }>(`/repos/${owner}/${name}/git/trees`, {
    token,
    method: 'POST',
    body: JSON.stringify({ tree: [{ path: DUMMY_FILE, mode: '100644', type: 'blob', sha: blobSha }] }),
  });

  const { sha: commitSha } = await createRootCommit({ owner, name }, 'init', treeSha, token);

  return commitSha;
}

async function createRootCommit({ owner, name }: RepoFullName, message: string, treeId: string, token: string) {
  return restRequest<{ sha: string }>(`/repos/${owner}/${name}/git/commits`, {
    method: 'POST',
    body: JSON.stringify({ message, tree: treeId }),
    token,
  });
}

async function restRequest<T>(path: string, init: CommonRequestInit & { token: string }) {
  const response = await rawRestRequest(path, init);

  if (!response.ok) {
    const text = await response.text().catch(() => '');

    throw new Error(`REST API error:\n${text}`, { cause: response });
  }

  return response.json<T>();
}

async function rawRestRequest(path: string, init: CommonRequestInit & { token: string }) {
  const { token, ...requestInit } = init;

  const response = await fetchImpl(`https://api.github.com${path}`, {
    ...requestInit,
    headers: {
      ...requestInit.headers,
      Authorization: `Bearer ${token}`,
    },
  });

  return response;
}

async function createCommitOnBranch(
  { repository: { owner, name }, branchName, expectedHeadOid }: CommitTarget,
  { commitMessage, fileChanges }: Commit,
  token: string,
) {
  const query = `
    mutation($input: CreateCommitOnBranchInput!) {
      createCommitOnBranch(input: $input) {
        commit {
          oid
        }
      }
    }
  `;

  const variables = {
    input: {
      branch: {
        repositoryNameWithOwner: `${owner}/${name}`,
        branchName,
      },
      expectedHeadOid,
      message: {
        headline: commitMessage,
      },
      fileChanges,
    },
  };

  const response = await graphqlRequest(query, variables, token);

  const { createCommitOnBranch } = await graphqlResponse<{ createCommitOnBranch: { commit: { oid: string } } }>(
    response,
  );

  return createCommitOnBranch;
}

async function forceUpdateRef({ refId, oid }: { refId: string; oid: string }, token: string) {
  const query = `
    mutation($input: UpdateRefInput!) {
      updateRef(input: $input) {
        clientMutationId
      }
    }
  `;

  const variables = {
    input: {
      refId,
      oid,
      force: true,
    },
  };

  const response = await graphqlRequest(query, variables, token);

  await graphqlResponse<{}>(response);
}

export async function createRef(
  { branchName, oid, repositoryId }: { repositoryId: string; oid: string; branchName: string },
  token: string,
) {
  const query = `
    mutation ($input: CreateRefInput!) {
      createRef(input: $input) {
        ref {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      repositoryId,
      name: `refs/heads/${branchName}`,
      oid,
    },
  };

  const response = await graphqlRequest(query, variables, token);

  const payload = await graphqlResponse<{ createRef: { ref: { id: string } } }>(response);

  return payload.createRef;
}

// FIXME: max number of repos, affiliation
export async function getRepositories(info: { organization: string }, token: string): Promise<Array<{ name: string }>>;
export async function getRepositories(token: string): Promise<Array<{ name: string }>>;
export async function getRepositories(infoOrToken: { organization: string } | string, token?: string) {
  token = typeof infoOrToken === 'string' ? infoOrToken : token;

  invariant(token);

  const organization = typeof infoOrToken === 'object' ? infoOrToken.organization : undefined;

  const viewerQuery = `
    query {
      viewer {
        repositories(first: 100, affiliations: [OWNER], orderBy: { field: UPDATED_AT, direction: DESC }) {
          edges {
            node {
              name,
              url
            }
          }
        }
      }
    }`;

  const orgQuery = `
    query($login: String!) {
      organization(login: $login) {
        repositories(first: 100) {
          nodes {
            name
          }
        }
      }
    }
  `;

  const response = await graphqlRequest(
    organization ? orgQuery : viewerQuery,
    organization ? { login: organization } : {},
    token,
  );

  if (organization) {
    const payload = await graphqlResponse<{ organization: { repositories: { nodes: Array<{ name: string }> } } }>(
      response,
    );

    return payload.organization.repositories.nodes;
  } else {
    const payload = await graphqlResponse<{ viewer: { repositories: { edges: Array<{ node: { name: string } }> } } }>(
      response,
    );

    return payload.viewer.repositories.edges.map((edge) => edge.node);
  }
}

async function graphqlRequest(query: string, variables: any, token: string) {
  return fetchImpl('https://api.github.com/graphql', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query, variables }),
  });
}

async function rawGraphqlResponse<T>(response: Response) {
  if (!response.ok) {
    const text = await response.text().catch(() => '');

    throw new Error(`Failed GraphQL request (${response.status}): ${text}`, { cause: response });
  }

  return response.json<T>();
}

async function graphqlResponse<T extends {}>(response: Response) {
  const payload = await rawGraphqlResponse<{ data: T } | { errors: any[]; data?: null }>(response);

  if ('errors' in payload) {
    throw new GraphQLError(payload.errors);
  }

  return payload.data;
}

interface GraphQLErrorPayload {
  type: string;
  message: string;
}

export class GraphQLError extends Error {
  name = 'GraphQLError';

  constructor(public errors: GraphQLErrorPayload[]) {
    super(`GraphQL API error\n${JSON.stringify(errors)}`);
  }

  contains(code: string) {
    return this.errors.some((error) => error.type === code);
  }
}

type CommonRequestInit = Omit<RequestInit, 'body'> & {
  body?: string;
};

async function fetchImpl(url: string | URL, init: CommonRequestInit) {
  if (import.meta.env.SSR) {
    const impl = await import('./server-fetch');

    const headers = new Headers(init.headers);

    headers.append('User-Agent', 'bolt.new');

    return impl.serverFetch(url, {
      ...init,
      headers,
    });
  } else {
    const impl = await import('./.client/fetch');

    return impl.clientFetch(url, init);
  }
}
